import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { PageHeader } from '../components/PageHeader';
import { workspacePath } from '../nav';
import { packageInstallUrl } from '../salesforce';
import { useSession } from '../session';

type Environment = 'production' | 'sandbox';

/**
 * The two places an org can live. Shown as two large cards rather than a dropdown, because the choice
 * decides where you sign in and which package you install, and a dropdown left on its default is the
 * easiest way to install into production by accident.
 */
const ENVIRONMENTS: { value: Environment; title: string; hint: string }[] = [
  {
    value: 'production',
    title: 'The live org, or a developer edition',
    hint: 'Signs in at login.salesforce.com.',
  },
  {
    value: 'sandbox',
    title: 'A copy for building and testing',
    hint: 'Signs in at test.salesforce.com.',
  },
];

/**
 * Connecting a Salesforce org, one step at a time.
 *
 * It used to be a label box, a dropdown and a button under a paragraph explaining that you had to install a
 * package and assign a permission set first — which is three prerequisites in prose above a form that does
 * not check any of them. People connected, hit an error deep in Salesforce, and came back with an org in
 * pending they could not use.
 *
 * The order here is the order it has to happen in, and nothing asks for a name: the org knows what it is
 * called, and the label is taken from it once we are inside and can be changed afterwards.
 */
export function NewOrg() {
  const { tenantId = '' } = useParams();
  const { me } = useSession();
  const [environment, setEnvironment] = useState<Environment>('production');
  const [installed, setInstalled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const workspaceName = me?.memberships.find((m) => m.tenantId === tenantId)?.name ?? 'Workspace';

  function connect() {
    setBusy(true);
    setError(null);
    api<{ authorizeUrl: string }>(`/api/tenants/${tenantId}/orgs/connect`, {
      method: 'POST',
      body: { environment },
    })
      .then((r) => {
        window.location.href = r.authorizeUrl;
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not start the connection');
        setBusy(false);
      });
  }

  return (
    <>
      <PageHeader
        crumbs={[
          { label: workspaceName, to: workspacePath(tenantId) },
          { label: 'Salesforce orgs', to: workspacePath(tenantId) },
          { label: 'Connect an org' },
        ]}
        title="Connect a Salesforce org"
        actions={
          <Link className="button secondary" to={workspacePath(tenantId)}>
            Back to the workspace
          </Link>
        }
      />
      <p className="lede">
        Three steps, in this order. Kruvi Access never asks for a password and never holds one: the last step signs
        you into Salesforce yourself, and what comes back is permission to mint sessions later.
      </p>
      {error && <div className="alert">{error}</div>}

      <div className="steps">
        <section className="card step done">
          <div className="n">1</div>
          <div>
            <h2 style={{ margin: '2px 0 6px' }}>Which kind of org is it?</h2>
            <p className="small muted">
              This decides where you sign in and which package link you need. A sandbox’s name comes across
              automatically, so you will be able to tell it from production afterwards.
            </p>
            <div className="choices" role="radiogroup" aria-label="Which kind of org">
              {ENVIRONMENTS.map((e) => (
                <label
                  key={e.value}
                  className={`choice ${e.value}${environment === e.value ? ' selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="environment"
                    value={e.value}
                    checked={environment === e.value}
                    onChange={() => {
                      setEnvironment(e.value);
                      // The other kind of org needs the other package link, so step 2 starts again.
                      setInstalled(false);
                    }}
                  />
                  <span className="choice-body">
                    <span className={`env-tag ${e.value}`}>{e.value === 'sandbox' ? 'Sandbox' : 'Production'}</span>
                    <span className="choice-title">{e.title}</span>
                    <span className="small muted">{e.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </section>

        <section className={`card step ${installed ? 'done' : ''}`}>
          <div className="n">2</div>
          <div>
            <h2 style={{ margin: '2px 0 6px' }}>Install the Kruvi Access package there</h2>
            <p className="small muted">
              Opens Salesforce in a new tab. Sign in to the org you are connecting — not to Kruvi Access — and
              install for all users. Then assign the <strong>Kruvi Access</strong> permission set to every
              user Kruvi Access should be able to act as, <strong>including the admin you are about to connect
              as</strong>. Salesforce refuses the connection otherwise, and that refusal is the most common
              reason this goes wrong.
            </p>
            <div className="actions">
              {/* A real link rather than a button that calls window.open, so the browser treats it as the
                  new tab it is and a pop-up blocker has nothing to object to. */}
              <a
                className="button secondary"
                href={packageInstallUrl(environment)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setInstalled(true)}
              >
                Open the package installer
              </a>
              {!installed && (
                <button type="button" className="secondary" onClick={() => setInstalled(true)}>
                  It is already installed
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="card step">
          <div className="n">3</div>
          <div>
            <h2 style={{ margin: '2px 0 6px' }}>Connect</h2>
            <p className="small muted">
              Signs you into that org as an admin and authorises Kruvi Access. Nothing is granted to anybody yet —
              you choose which Salesforce users Kruvi Access may act as, and who may use them, on the next
              screen.
            </p>
            <button type="button" className="block" onClick={connect} disabled={busy || !installed}>
              {busy
                ? 'Starting…'
                : `Connect to Salesforce ${environment === 'sandbox' ? '(sandbox)' : '(production)'}`}
            </button>
            {!installed && (
              <p className="small muted" style={{ marginBottom: 0 }}>
                Install the package first.
              </p>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
